import {
    CombinedState,
    Reducer,
    ReducersMapObject,
    configureStore,
} from '@reduxjs/toolkit';

import { catalogItemReducer } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { categoryReducer } from 'entities/Category/model/slices/CategorySlice';
import { NotificationReducer } from 'entities/Notification/model/slices/NotificationsSlice';
import { orderReducer } from 'entities/Order/model/slices/OrderSlice';
import { productSourcingReducer } from 'entities/ProductSourcing/model/slices/ProductSourcingSlice';
import { propertyReducer } from 'entities/Property/model/slices/PropertySlice';
import { reportSavingsSummaryReducer } from 'entities/ReportSavingsSummary/model/slices/ReportSavingsSummarySlice';
import { reportSpendByCategoryReducer } from 'entities/ReportSpendByCategory/model/slices/ReportSpendByCategorySlice';
import { supplierReducer } from 'entities/Supplier/model/slices/SupplierSlice';
import { userReducer } from 'entities/User';
import { vendorsReducer } from 'entities/Vendors/model/slices/VendorsSlice';
import { addCatalogItemReducer } from 'features/AddCatalogItem/model/slices/AddCatalogItemSlice';
import { addInventoryItemReducer } from 'features/AddInventoryItem/model/slices/AddInventoryItemSlice';
import { addItemsToOrderReducer } from 'features/AddItemsToOrder/model/slices/addItemsToOrderSlice';
import { editMinAllowedInStockReducer } from 'features/EditMinAllowedInStock/model/slices/EditMinAllowedInStockSlice';
import { editOrderItemSourcingReducer } from 'features/EditOrderItemSourcing/model/slices/EditOrderItemSourcingSlice';
import { getLabelsReducer } from 'features/GetLabelsPrint/model/slices/getLabelsSlice';
import { navigationBlockModalReducer } from 'features/NavigationBlockModal/model/slices/NavigationBlockModalSlice';
import { NotificationsReducer } from 'features/Notifications/model/slices/NotificationsSlice';
import { orderProposalFilterReducer } from 'features/OrderProposalFilter/model/slices/OrderProposalFilterSlice';
import { profileReducer } from 'features/ProfileCard/model/slices/ProfileCardSlice';
import { removeInventoryItemReducer } from 'features/RemoveInventoryItem/model/slices/RemoveInventoryItemSlice';
import { reorderInventoryItemReducer } from 'features/ReorderInventoryItem/model/slices/ReorderInventoryItemSlice';
import { searchCatalogItemsReducer } from 'features/SearchCatalogItems/model/slices/SearchCatalogItemsSlice';
import { fetchPropertyByIdReducer } from 'features/fetchPropertyById/model/slices/FetchPropertyByIdSlice';
import { moveToInventoryItemReducer } from 'features/moveToInventoryCatalogItem/model/slices/MoveToInventoryItemSlice';
import { removeCatalogItemReducer } from 'features/removeCatalogItem/model/slices/RemoveCatalogItemSlice';
import { $api } from 'shared/api/api';
import { rtkApi } from 'shared/api/rtkApi';
import { inventoryTableReducer } from 'widgets/InventoryTable/model/slices/InventoryTableSlice';
import { orderTableReducer } from 'widgets/OrdersTable/model/slices/OrderTableSlice';

import { StateSchema } from './StateSchema';
import { createReducerManager } from './reducerManager';
import { ThunkExtraArg } from './types';

export function createReduxStore(
    initialState?: StateSchema,
    asyncReducers?: ReducersMapObject<StateSchema>,
) {
    const rootReducers: ReducersMapObject<StateSchema> = {
        ...asyncReducers,
        user: userReducer,
        profile: profileReducer,

        property: propertyReducer,
        propertyItem: fetchPropertyByIdReducer,

        category: categoryReducer,

        getLabelsForDownload: getLabelsReducer,
        catalogItem: catalogItemReducer,
        addCatalogItem: addCatalogItemReducer,
        removeCatalogItem: removeCatalogItemReducer,
        searchCatalogItems: searchCatalogItemsReducer,
        editMinAllowedInStock: editMinAllowedInStockReducer,

        addInventoryItem: addInventoryItemReducer,
        removeInventoryItem: removeInventoryItemReducer,
        moveToInventoryItem: moveToInventoryItemReducer,
        reorderInventoryItem: reorderInventoryItemReducer,

        notifications: NotificationsReducer,
        notification: NotificationReducer,

        order: orderReducer,
        orderTable: orderTableReducer,
        orderProposalFilter: orderProposalFilterReducer,

        editOrderItemSourcing: editOrderItemSourcingReducer,

        inventoryTable: inventoryTableReducer,
        addItemsToOrder: addItemsToOrderReducer,
        navigationBlock: navigationBlockModalReducer,
        reportSpendByCategory: reportSpendByCategoryReducer,
        reportSavingsSummary: reportSavingsSummaryReducer,
        productSourcing: productSourcingReducer,
        vendors: vendorsReducer,
        supplier: supplierReducer,
    };

    const reducerManager = createReducerManager(rootReducers);

    const extraArg: ThunkExtraArg = {
        api: $api,
    };

    const store = configureStore({
        reducer: reducerManager.reduce as Reducer<CombinedState<StateSchema>>,
        preloadedState: initialState,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                thunk: {
                    extraArgument: extraArg,
                },
            }).concat(rtkApi.middleware),
    });

    // @ts-ignore
    store.reducerManager = reducerManager;

    return store;
}

export type AppDispatch = ReturnType<typeof createReduxStore>['dispatch'];
